import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, ErrorBoundary, FlexBox, Icon, Text, } from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import { useCallback } from 'react';
import { useToggle, useUpdateEffect } from 'react-use';
import { analytics } from 'app/utils/segment';
import { useBreakpointValue } from 'utils/mediaQuery';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { analyticsActions } from '../../analytics.actions';
import { useAnalyticsState } from '../panels/utils/useAnalyticsState/useAnalyticsState';
import styles from './analyticsControls.module.scss';
import { AnalyticsContext } from './components/context/analyticsContext';
import { AnalyticsFilters } from './components/filters/analyticsFilters';
import { AnalyticsPeriod } from './components/period/analyticsPeriod';
export const AnalyticsControls = () => {
    const { dashboard, context, period, settings } = useAnalyticsState();
    const dispatch = useAppDispatch();
    const isSmallScreen = useBreakpointValue({ base: true, md: false });
    const [hasCollapsedFilters, toggleHasCollapseFilters] = useToggle(!!isSmallScreen);
    useUpdateEffect(() => {
        toggleHasCollapseFilters(!!isSmallScreen);
    }, [isSmallScreen]);
    /** Update the selected context in the store. */
    const handleChangeContext = useCallback((context) => {
        dispatch(analyticsActions.setContext({ context }));
        analytics.analyticsContextUpdated({
            dashboard: dashboard.title,
        });
    }, [dashboard.title, dispatch]);
    /** Update the selected period in the store. */
    const handleChangePeriod = (period) => {
        dispatch(analyticsActions.setPeriod({ period }));
        analytics.analyticsPeriodUpdated({
            dashboard: dashboard.title,
            value: period.values.length === 1
                ? period.values[0].value
                : 'dateWithDefaultTime',
            timezone: settings.timezone,
        });
    };
    /** Update the selected settings in the store. */
    const handleChangeSettings = (newSettings) => {
        dispatch(analyticsActions.setSettings({
            settings: Object.assign(Object.assign({}, settings), newSettings),
        }));
        if (newSettings.timezone !== settings.timezone) {
            analytics.analyticsTimezoneUpdated({
                dashboard: dashboard.title,
                timezone: newSettings.timezone,
            });
        }
        if (newSettings.schedule_id !== settings.schedule_id) {
            if (newSettings.schedule_id) {
                analytics.analyticsBusinessHoursScheduleUpdated({
                    dashboard: dashboard.title,
                    timezone: newSettings.timezone,
                });
            }
            else {
                analytics.analyticsBusinessHoursScheduleRemoved({
                    dashboard: dashboard.title,
                    timezone: settings.timezone,
                });
            }
        }
    };
    return (_jsx(ErrorBoundary, Object.assign({ fallback: _jsxs(Text.Paragraph, Object.assign({ color: colors.red[500] }, { children: ["Filters are currently unavailable due to an unexpected error. Our team has been notified and is looking into the issue.", ' ', _jsx("a", Object.assign({ href: "#", onClick: () => window.location.reload() }, { children: "Try reloading." }))] })) }, { children: _jsxs("div", Object.assign({ className: styles.controls }, { children: [_jsxs(FlexBox, Object.assign({ gap: "300", alignItems: "center" }, { children: [dashboard.context && (_jsx(AnalyticsContext, { context: dashboard.context, control: context, onChange: handleChangeContext })), _jsx(AnalyticsPeriod, { period: period, settings: settings, onChangePeriod: handleChangePeriod, onChangeSettings: handleChangeSettings })] })), isSmallScreen && (_jsx(Button, Object.assign({ variant: "tertiary", slotStart: _jsx(Icon, { name: hasCollapsedFilters ? 'plus' : 'minus' }), onClick: toggleHasCollapseFilters }, { children: hasCollapsedFilters ? 'More filters' : 'Less filters' }))), dashboard.filters.length > 0 && (_jsx(AnalyticsFilters, { dashboardFilters: dashboard.filters, isCollapsed: hasCollapsedFilters }))] })) })));
};
